import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';

import {
    FaTrophy,
    FaCalendarAlt,
    FaUsers,
    FaMedal,
    FaAward,
    FaCrown,
    FaCoins,
    FaChartBar,
    FaClock,
    FaCheckCircle,
    FaSearch,
    FaChevronLeft,
    FaChevronRight,
    FaFire,
    FaStar,
    FaEye,
    FaBolt
} from 'react-icons/fa';
import {
    MdLeaderboard,
    MdQuiz,
    MdTimer,
    MdDashboard
} from 'react-icons/md';
import {
    IoTrophy,
    IoStatsChart,
    IoTime,
    IoCheckmarkCircle
} from 'react-icons/io5';
import {
    HiSparkles,
    HiLightningBolt,
    HiTrendingUp
} from 'react-icons/hi';
import formatDateTime from '../../components/formatDateTime';

const QuizLeaderboard = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loader, setLoader] = useState(false);
    const [quizData, setQuizData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [timeUntilMidnight, setTimeUntilMidnight] = useState('');

    // Format seconds to MM:SS or HH:MM:SS
    const formatTime = (seconds) => {
        if (!seconds) return '00:00';

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        // If hours is 0, only show minutes and seconds (MM:SS)
        if (hours === 0) {
            return [
                minutes.toString().padStart(2, '0'),
                secs.toString().padStart(2, '0')
            ].join(':');
        }

        // Otherwise show hours, minutes and seconds (HH:MM:SS)
        return [
            hours.toString().padStart(2, '0'),
            minutes.toString().padStart(2, '0'),
            secs.toString().padStart(2, '0')
        ].join(':');
    };

    const type = searchParams.get('type') || 'daily';

    // Calculate time until midnight for daily quiz reset
    useEffect(() => {
        const updateCountdown = () => {
            const now = new Date();
            const midnight = new Date();
            midnight.setHours(24, 0, 0, 0); // Next midnight

            const timeDiff = midnight - now;
            const hours = Math.floor(timeDiff / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

            setTimeUntilMidnight(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        };

        if (type === 'daily') {
            updateCountdown();
            const interval = setInterval(updateCountdown, 1000);
            return () => clearInterval(interval);
        }
    }, [type]);

    const fetchQuizData = async (page = 1) => {
        setLoader(true);
        try {
            let response;
            if (type === 'daily') {
                response = await Calling.getAdminDailyDashboard(id, page, itemsPerPage);
            } else if (type === 'live') {
                response = await Calling.getAdminLiveDashboard(id, page, itemsPerPage);
            } else {
                response = await Calling.getAdminDailyDashboard(id, page, itemsPerPage);
            }

            if (response.status) {
                // Set up dynamic coin rewards
                const data = { ...response.data };
                data.winCoins = [
                    { rank: 1, coin: 1000 },
                    { rank: 2, coin: 500 },
                    { rank: 3, coin: 500 }
                ];

                setQuizData(data);
                if (data.pagination) {
                    setTotalPages(data.pagination.pages);
                }
            } else {
                toast.error(response.message || 'Failed to fetch quiz data');
            }
        } catch (error) {
            console.error('Error fetching quiz:', error);
            toast.error('Error fetching quiz data');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchQuizData(currentPage);
        }
    }, [id, type, currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getCoinPrize = (rank) => {
        if (!quizData || !quizData.winCoins) return 0;
        const prize = quizData.winCoins.find(prize => prize.rank === rank);
        return prize ? prize.coin : 0;
    };
    const handleEdit = (id) => {
        navigate(`/manage/user/all/info/${id}`);
    };

    const getRankBadge = (rank) => {
        switch (rank) {
            case 1: return <FaTrophy className="w-8 h-8 text-gold" />;
            case 2: return <FaMedal className="w-8 h-8 text-silver" />;
            case 3: return <FaAward className="w-8 h-8 text-bronze" />;
            default: return <FaStar className="w-6 h-6 text-main-blue" />;
        }
    };

    const getRankColors = (rank) => {
        switch (rank) {
            case 1: return 'from-gold to-gold/80';
            case 2: return 'from-silver to-silver/80';
            case 3: return 'from-bronze to-bronze/80';
            default: return 'from-main-blue to-main-blue/80';
        }
    };

    if (loader) {
        return (
            <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
                <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Quiz Management</h1>
                <div className='bg-main-white rounded-xl shadow-sm border border-main-border overflow-hidden'>
                    <Navbar selectState={'quizshow'} />
                    <hr className='border-main-border' />
                    <div className="flex justify-center items-center p-20">
                        <div className="text-center">
                            <div className="animate-spin w-16 h-16 border-4 border-main-blue/20 border-t-main-blue rounded-full mx-auto mb-6"></div>
                            <p className="text-main-subfont text-lg font-medium">Loading quiz data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!quizData) {
        return (
            <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
                <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Quiz Management</h1>
                <div className='bg-main-white rounded-xl shadow-sm border border-main-border overflow-hidden'>
                    <Navbar selectState={'quizshow'} />
                    <hr className='border-main-border' />
                    <div className="text-center py-20">
                        <FaSearch className="w-20 h-20 mx-auto mb-6 text-main-subfont" />
                        <p className="text-2xl text-main-font font-medium">No quiz data available</p>
                        <p className="text-main-subfont mt-2">Check back later for quiz results</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Quiz Management</h1>
            <div className='bg-main-white rounded-xl shadow-sm border border-main-border overflow-hidden'>
                <Navbar selectState={'quizshow'} />
                <hr className='border-main-border' />

                {/* Quiz Type Selector with Timer */}
                <div className="p-4">
                    <div className="flex justify-center">
                        <div className="bg-main-bg p-2 rounded-xl flex space-x-2 shadow-sm">
                            {type === 'daily' ? (
                                <div className="px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-main-blue to-primary-purple text-white shadow-md flex items-center gap-3">
                                    <FaCalendarAlt className="w-5 h-5" />
                                    <div className="flex flex-col items-center">
                                        <span>Daily Quiz</span>
                                        <div className="flex items-center gap-2 text-white/80 text-sm">
                                            <FaClock className="w-3 h-3" />
                                            <span>Resets in: {timeUntilMidnight}</span>
                                        </div>
                                    </div>
                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                </div>
                            ) : (
                                ''
                            )}

                            {type === 'live' ? (
                                <div className="px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-main-delete to-main-delete/80 text-white shadow-md flex items-center gap-3">
                                    <FaBolt className="w-5 h-5" />
                                    <span>Live Quiz</span>
                                    <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                                </div>
                            ) : (
                                ''
                            )}
                        </div>
                    </div>
                </div>

                {/* Quiz Info Card */}
                <div className="bg-gradient-to-r from-main-th to-main-bg p-6 rounded-xl border border-main-border mb-8 mx-4 shadow-sm">
                    <div className="text-center mb-6">
                        <h2 className="text-2xl font-bold text-main-font flex items-center justify-center gap-3">
                            <MdQuiz className="w-7 h-7 text-main-blue" />
                            {quizData.quizTitle}
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 gap-6 items-center justify-center">
                        <div className="text-center">
                            <div className="bg-main-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 border border-main-border">
                                <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-main-blue to-primary-purple rounded-xl mb-4 shadow-md">
                                    <FaUsers className="w-7 h-7 text-white" />
                                </div>
                                <p className="text-sm text-main-subfont font-medium mb-2">Total Participants</p>
                                <p className="text-xl font-bold text-main-font">
                                    {quizData.totalParticipants.toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Champions Podium */}
                {quizData.leaderboard && quizData.leaderboard.length > 0 && (
                    <div className="mb-12 px-4">
                        <div className="text-center mb-8">
                            <h3 className="text-xl font-bold text-main-font flex items-center justify-center gap-3 mb-2">
                                <div className="p-2 bg-gradient-to-r from-main-yellow to-main-yellow/80 rounded-xl shadow-md">
                                    <FaTrophy className="w-6 h-6 text-white" />
                                </div>
                                Champions Podium
                            </h3>
                            <p className="text-main-subfont">Celebrating our top performers</p>
                        </div>

                        <div className="flex justify-center items-end space-x-8 h-64 perspective-1000">
                            {/* Second Place */}
                            {quizData.leaderboard.find(user => user.rank === 2) && (
                                <div className="flex flex-col items-center animate-float" style={{ animationDelay: '0.2s' }}>
                                    <div className="relative mb-4">
                                        <div className="w-20 h-20 rounded-full border-4 border-silver overflow-hidden shadow-md bg-silver/20 relative z-10">
                                            {quizData.leaderboard.find(user => user.rank === 2).profile ? (
                                                <img
                                                    src={quizData.leaderboard.find(user => user.rank === 2).profile}
                                                    alt="2nd Place"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-main-font">
                                                    {quizData.leaderboard.find(user => user.rank === 2).name.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                        <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-silver to-silver/80 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg shadow-md z-20">
                                            2
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-t from-silver/80 to-silver/60 rounded-t-xl w-28 h-32 flex flex-col items-center justify-center shadow-md relative overflow-hidden">
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                                        <FaMedal className="w-8 h-8 mb-2 text-white relative z-10" />
                                        <p className="text-sm font-bold text-center text-white px-2 relative z-10 mb-1 truncate w-full">
                                            {quizData.leaderboard.find(user => user.rank === 2).name}
                                        </p>
                                        <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 relative z-10">
                                            <FaCoins className="w-4 h-4 text-main-yellow mr-1" />
                                            <span className="font-bold text-sm text-main-font">{getCoinPrize(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* First Place */}
                            {quizData.leaderboard.find(user => user.rank === 1) && (
                                <div className="flex flex-col items-center animate-float">
                                    <div className="relative mb-4">
                                        <div className="w-24 h-24 rounded-full border-4 border-gold overflow-hidden shadow-md bg-gold/20 relative z-10">
                                            {quizData.leaderboard.find(user => user.rank === 1).profile ? (
                                                <img
                                                    src={quizData.leaderboard.find(user => user.rank === 1).profile}
                                                    alt="1st Place"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-main-font">
                                                    {quizData.leaderboard.find(user => user.rank === 1).name.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                            <FaCrown className="w-8 h-8 text-gold animate-pulse" />
                                        </div>
                                        <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-gold to-gold/80 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-xl shadow-md z-20">
                                            1
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-t from-gold/80 to-gold/60 rounded-t-xl w-32 h-40 flex flex-col items-center justify-center shadow-md relative overflow-hidden">
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                                        <HiSparkles className="w-10 h-10 mb-2 text-white relative z-10 animate-pulse" />
                                        <p className="text-base font-bold text-center text-white px-2 relative z-10 mb-2 truncate w-full">
                                            {quizData.leaderboard.find(user => user.rank === 1).name}
                                        </p>
                                        <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 relative z-10">
                                            <FaCoins className="w-5 h-5 text-main-yellow mr-1" />
                                            <span className="font-bold text-main-font">{getCoinPrize(1)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Third Place */}
                            {quizData.leaderboard.find(user => user.rank === 3) && (
                                <div className="flex flex-col items-center animate-float" style={{ animationDelay: '0.4s' }}>
                                    <div className="relative mb-4">
                                        <div className="w-18 h-18 rounded-full border-4 border-bronze overflow-hidden shadow-md bg-bronze/20 relative z-10">
                                            {quizData.leaderboard.find(user => user.rank === 3).profile ? (
                                                <img
                                                    src={quizData.leaderboard.find(user => user.rank === 3).profile}
                                                    alt="3rd Place"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-xl font-bold text-main-font">
                                                    {quizData.leaderboard.find(user => user.rank === 3).name.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                        <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-bronze to-bronze/80 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold shadow-md z-20">
                                            3
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-t from-bronze/80 to-bronze/60 rounded-t-xl w-24 h-24 flex flex-col items-center justify-center shadow-md relative overflow-hidden">
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                                        <FaAward className="w-7 h-7 mb-1 text-white relative z-10" />
                                        <p className="text-xs font-bold text-center text-white px-2 relative z-10 mb-1 truncate w-full">
                                            {quizData.leaderboard.find(user => user.rank === 3).name}
                                        </p>
                                        <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 relative z-10">
                                            <FaCoins className="w-3 h-3 text-main-yellow mr-1" />
                                            <span className="font-bold text-xs text-main-font">{getCoinPrize(3)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Leaderboard Table */}
                <div className="bg-main-bg rounded-xl p-6 mx-4 shadow-sm border border-main-border mb-6">
                    <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-main-font flex items-center justify-center gap-3 mb-2">
                            <div className="p-2 bg-gradient-to-r from-main-blue to-primary-purple rounded-xl shadow-md">
                                <MdLeaderboard className="w-5 h-5 text-white" />
                            </div>
                            Complete Leaderboard
                        </h3>
                        <p className="text-main-subfont">Detailed performance analytics</p>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-main-white rounded-xl overflow-hidden shadow-sm border border-main-border">
                            <thead>
                                <tr className="bg-gradient-to-r from-main-blue via-primary-purple to-secondary-teal text-white">
                                    <th className="px-6 py-4 text-left font-bold text-sm uppercase tracking-wider">
                                        <div className="flex items-center gap-2">
                                            <HiTrendingUp className="w-4 h-4" />
                                            Rank
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-left font-bold text-sm uppercase tracking-wider">
                                        <div className="flex items-center gap-2">
                                            <FaUsers className="w-4 h-4" />
                                            Participant
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-left font-bold text-sm uppercase tracking-wider">
                                        <div className="flex items-center gap-2">
                                            <FaCheckCircle className="w-4 h-4" />
                                            Score
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-left font-bold text-sm uppercase tracking-wider">
                                        <div className="flex items-center gap-2">
                                            <MdTimer className="w-4 h-4" />
                                            Duration
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-left font-bold text-sm uppercase tracking-wider">
                                        <div className="flex items-center gap-2">
                                            <FaClock className="w-4 h-4" />
                                            Submitted
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-right font-bold text-sm uppercase tracking-wider">
                                        <div className="flex items-center justify-end gap-2">
                                            <FaCoins className="w-4 h-4" />
                                            Reward
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-main-border">
                                {quizData.leaderboard && quizData.leaderboard.length > 0 ? (
                                    quizData.leaderboard.map((user, index) => (
                                        <tr
                                            key={user.userId}
                                            className={`transition-all duration-300 hover:bg-main-th ${user.rank === 1
                                                ? 'bg-gold/5 border-l-4 border-gold'
                                                : user.rank === 2
                                                    ? 'bg-silver/5 border-l-4 border-silver'
                                                    : user.rank === 3
                                                        ? 'bg-bronze/5 border-l-4 border-bronze'
                                                        : 'hover:border-l-4 hover:border-main-blue border-l-4 border-transparent'
                                                }`}
                                            onClick={() => handleEdit(user.userId)}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white shadow-sm ${user.rank <= 3
                                                        ? 'bg-gradient-to-r ' + getRankColors(user.rank)
                                                        : 'bg-gradient-to-r from-main-subfont to-main-subfont/80'
                                                        }`}>
                                                        {user.rank}
                                                    </div>
                                                    <div className="text-lg">
                                                        {getRankBadge(user.rank)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    {user.profile ? (
                                                        <div className="relative">
                                                            <img
                                                                src={user.profile}
                                                                alt={user.name}
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNFNUU3RUIiLz4KPHBhdGggZD0iTTIwIDE4QzIyLjIwOTEgMTggMjQgMTYuMjA5MSAyNCAxNEMyNCAxMS43OTA5IDIyLjIwOTEgMTAgMjAgMTBDMTcuNzkwOSAxMCAxNiAxMS43OTA5IDE2IDE0QzE2IDE2LjIwOTEgMTcuNzkwOSAxOCAyMCAxOFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHBhdGggZD0iTTI4IDI4QzI4IDI0LjY4NjMgMjQuNDE4MyAyMiAyMCAyMkMxNS41ODE3IDIyIDEyIDI0LjY4NjMgMTIgMjhIMjhaIiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPg==';

                                                                }}
                                                                className="w-12 h-12 rounded-xl border-2 border-main-white shadow-sm object-cover"
                                                            />
                                                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
                                                        </div>
                                                    ) : (
                                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-main-blue to-primary-purple flex items-center justify-center text-white font-bold text-xl shadow-sm relative">
                                                            {user.name.charAt(0).toUpperCase()}
                                                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="font-bold text-main-font">{user.name}</p>
                                                        <p className="text-sm text-main-subfont">{user.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="inline-flex items-center gap-2 bg-green-100/50 text-green-700 px-3 py-2 rounded-xl font-bold shadow-sm border border-green-200/50">
                                                    <IoCheckmarkCircle className="w-5 h-5" />
                                                    {user.correctAns}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="inline-flex items-center gap-2 bg-main-th text-main-blue px-3 py-2 rounded-xl font-bold shadow-sm border border-main-blue/10">
                                                    <IoTime className="w-5 h-5" />
                                                    {formatTime(user.timeTaken)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-main-subfont font-medium">
                                                {new Date(user.submittedAt).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                {getCoinPrize(user.rank) > 0 ? (
                                                    <div className="inline-flex items-center gap-2 bg-main-yellow/10 text-main-font px-3 py-2 rounded-xl font-bold shadow-sm border border-main-yellow/20">
                                                        <FaCoins className="w-5 h-5 text-main-yellow" />
                                                        {getCoinPrize(user.rank).toLocaleString()}
                                                    </div>
                                                ) : (
                                                    <span className="text-main-subfont font-medium bg-main-bg px-3 py-2 rounded-xl">No reward</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-16 text-center">
                                            <div className="flex flex-col items-center">
                                                <FaSearch className="w-16 h-16 text-main-subfont/30 mb-4" />
                                                <p className="text-xl text-main-font font-medium">No participants found</p>
                                                <p className="text-main-subfont mt-2">Check back later for results</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                {quizData.pagination && quizData.pagination.pages > 1 && (
                    <div className="flex justify-center my-8">
                        <nav className="flex items-center space-x-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 ${currentPage === 1
                                    ? 'bg-main-bg text-main-subfont cursor-not-allowed'
                                    : 'bg-main-white text-main-font hover:bg-main-blue hover:text-white border border-main-border hover:border-main-blue shadow-sm'
                                    }`}
                            >
                                <FaChevronLeft className="w-3 h-3" />
                                Previous
                            </button>

                            {[...Array(quizData.pagination.pages)].map((_, index) => {
                                const pageNum = index + 1;
                                if (
                                    pageNum === 1 ||
                                    pageNum === quizData.pagination.pages ||
                                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                ) {
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            className={`w-10 h-10 rounded-lg font-bold transition-all duration-300 ${currentPage === pageNum
                                                ? 'bg-main-blue text-white shadow-md'
                                                : 'bg-main-white text-main-font hover:bg-main-th border border-main-border'
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                } else if (
                                    pageNum === currentPage - 2 ||
                                    pageNum === currentPage + 2
                                ) {
                                    return <span key={pageNum} className="px-2 text-main-subfont font-bold">...</span>;
                                }
                                return null;
                            })}

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === quizData.pagination.pages}
                                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 ${currentPage === quizData.pagination.pages
                                    ? 'bg-main-bg text-main-subfont cursor-not-allowed'
                                    : 'bg-main-white text-main-font hover:bg-main-blue hover:text-white border border-main-border hover:border-main-blue shadow-sm'
                                    }`}
                            >
                                Next
                                <FaChevronRight className="w-3 h-3" />
                            </button>
                        </nav>
                    </div>
                )}

                {/* Footer Stats */}
                <div className="text-center pb-8">
                    <div className="inline-flex items-center bg-main-th px-6 py-3 rounded-xl border border-main-border shadow-sm">
                        <IoStatsChart className="w-5 h-5 text-main-blue mr-2" />
                        <span className="text-main-font font-bold">
                            Showing {quizData.leaderboard ? quizData.leaderboard.length : 0} of {quizData.totalParticipants.toLocaleString()} participants
                        </span>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes float {
                    0% { transform: translateY(0px); }
                    50% { transform: translateY(-10px); }
                    100% { transform: translateY(0px); }
                }
                .animate-float {
                    animation: float 3s ease-in-out infinite;
                }
                .perspective-1000 {
                    perspective: 1000px;
                }
            `}</style>
        </div>
    );
};

export default QuizLeaderboard;